import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableContainer,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { api_host } from "../../../../common/globals/constants";
import { useObjectivesList } from "../../../../hooks/useObjectivesList";
import { usePasosObjetivo } from "../../../../hooks/usePasosObjetivo";
import { ObjectiveSelect } from "./../../../../components/ObjectiveSelect";
import { FormPaso } from "./FormPaso";
import { ApdexConfigWarning } from "../../../../components/Apdex/ApdexConfigWarning";

export const ApdexFormContext = createContext(null);
export const ApdexFormDispatchContext = createContext(null);

export function formPasoReducer(state, action) {
  switch (action.type) {
    case "SET_PASO_STATUS":
      state[action.paso] = action.formStatus;
      return state;
    case "SET_DEFAULT":
      state = {};
      return state;
    default:
      throw Error("Unknown action.");
  }
}

function Apdex({ defaultObjective, ...props }) {
  const [state, dispatch] = useReducer(formPasoReducer, {});

  return (
    <ApdexFormContext.Provider value={state}>
      <ApdexFormDispatchContext.Provider value={dispatch}>
        <ApdexForm defaultObjective={defaultObjective} {...props} />
      </ApdexFormDispatchContext.Provider>
    </ApdexFormContext.Provider>
  );
}

function ApdexForm({ defaultObjective, ...props }) {
  const { t } = props;
  const {
    data: objectivesList,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });
  const [objectiveId, setObjectiveId] = useState("");
  const {
    data: listadoPasos,
    isLoading: isLoadingListadoPasos,
    refetch: refetchPasosObjetivo,
  } = usePasosObjetivo({ objetivoId: objectiveId });
  const [pasosEditable, setPasosEditable] = useState(null);
  const state = useContext(ApdexFormContext);
  const [sendingData, setSendingData] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    severity: "success",
    message: "",
  });
  const dispatch = useContext(ApdexFormDispatchContext);

  useEffect(() => {
    if (objectivesList.length && defaultObjective) {
      setObjectiveId(defaultObjective);
    }
  }, [objectivesList, defaultObjective]);

  function handleChangeObjective(newValue) {
    dispatch({
      type: "SET_DEFAULT",
    });
    setObjectiveId(newValue);
  }

  function changePasosEditable({ indice, groupKey, key, value }) {
    const paso = listadoPasos[indice];
    paso[groupKey][key] = value;
    const nuevoEditable = [...listadoPasos];
    nuevoEditable[indice] = paso;
    setPasosEditable(nuevoEditable);
  }

  async function saveConfiguration() {
    if (validateDisableButton()) {
      alert(t('apdex.alert.formErrors'));
      return;
    }
    setSendingData(true);
    const pasosGuardar = pasosEditable ?? listadoPasos;
    const pasos = pasosGuardar.map(({ umbrales, paso_orden, paso_nombre }) => {
      return { umbrales, paso_orden, paso_nombre };
    });
    try {
      const requestData = {
        apdex: {
          pasos,
          objetivo: objectiveId,
        },
      };
      let token = sessionStorage.getItem("key");
      const { status, statusText } = await fetch(
        `${api_host}/reporte/online/indicadores/updateIndicador/?objetivo=${objectiveId}&token=${token}&indicadores=${JSON.stringify(
          requestData
        )}`
      );
      if (status === 200) {
        setSnackbarData({
          severity: "success",
          message: t('apdex.snackbar.saveSuccess')
        });
      } else {
        throw new Error(statusText);
      }
    } catch (err) {
      console.error(err);
      setSnackbarData({
        severity: "error",
        message: t('apdex.snackbar.saveError')
      });
    } finally {
      setSendingData(false);
      setOpenSnackbar(true);
    }
  }

  const deleteUmbrales = async () => {
    const confirmDeleteUmbrales = window.confirm(
      t('apdex.confirm.deleteUmbrales')
    );
    if (!confirmDeleteUmbrales) {
      return false;
    }
    setSendingData(true);
    try {
      let token = sessionStorage.getItem("key");
      const { status } = await fetch(
        `${api_host}/reporte/online/indicadores/deleteUmbrales/?token=${token}&objetivo=${objectiveId}`
      );
      if (status === 200) {
        setSnackbarData({
          severity: "success",
          message: t('apdex.snackbar.saveSuccess')
        });
        refetchPasosObjetivo();
      }
    } catch (err) {
      console.error(err);
      setSnackbarData({
        severity: "error",
        message: t('apdex.snackbar.saveError')
      });
    } finally {
      setSendingData(false);
      setOpenSnackbar(true);
    }
  };

  const validateDisableButton = () => {
    const validarPasosEditable = pasosEditable ?? listadoPasos;
    if (!validarPasosEditable) return true;
    let validadorUmbrales = true;
    for (const { umbrales } of validarPasosEditable) {
      if (
        !umbrales.sla_error_apdex ||
        !umbrales.sla_error_tiempo ||
        !umbrales.sla_ok_apdex ||
        !umbrales.sla_ok_tiempo
      ) {
        validadorUmbrales = false;
        break;
      }
    }
    if (!validadorUmbrales) return true;
    const values = Object.values(state);
    return values.includes(false);
  };

  return (
    <Card>
      <CardContent>
        <Button variant="text"
          color="primary"
          style={{ marginBottom: 10 }}
          onClick={() => props.history.goBack()}>
            {t('apdex.back')}
        </Button>
        <Typography variant="h4">{t('apdex.title')}</Typography>
        <Typography variant="body1">{t('apdex.subtitle')}</Typography>
        <Typography variant="body2">{t('apdex.description.p1')}</Typography>
        <Typography variant="body2">{t('apdex.description.p2')}</Typography>
        <Box marginTop={2}>
          <Typography variant="body2">
            <b>{t('apdex.types.satisfied.label')}</b>{t('apdex.types.satisfied.desc')}
          </Typography>
          <Typography variant="body2">
            <b>{t('apdex.types.tolerable.label')}</b>{t('apdex.types.tolerable.desc')}
          </Typography>
          <Typography variant="body2">
            <b>{t('apdex.types.frustrating.label')}</b>{t('apdex.types.frustrating.desc')}
          </Typography>
        </Box>
      </CardContent>
      <Box padding={2}>
        <Grid container spacing={1}>
          <Grid item lg={3} md={3} sm={6} xs={12}>
            <ObjectiveSelect
              objetivos={objectivesList}
              objetivoId={objectiveId}
              setObjetivoId={handleChangeObjective}
              isLoading={isLoadingObjectiveList}
            />
            {isLoadingObjectiveList && (
              <Typography variant="body2" style={{ marginTop: 5 }}>
                {t('apdex.loadingObjectives')}
              </Typography>
            )}
          </Grid>
          {objectiveId && listadoPasos.length ? (
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <Button
                variant="contained"
                style={{ backgroundColor: "#d13636", color: "white" }}
                onClick={() => deleteUmbrales()}
              >
                {t('apdex.deleteIndicators')}
              </Button>
            </Grid>
          ) : null}
        </Grid>
        <Box marginTop={2}>
          <ApdexConfigWarning objetivoId={objectiveId} />
        </Box>
        {!isLoadingListadoPasos && (
          <>
            <Grid item lg={12} md={12} sm={12} style={{ marginTop: "20px" }}>
              {!listadoPasos && (
                <Alert severity="info">
                  {t('apdex.alert.noDataRange')}
                </Alert>
              )}
              {listadoPasos && (
                <TableContainer style={{ width: "100%" }}>
                  <Table style={{ minWidth: 1000 }}>
                    <TableBody>
                      {listadoPasos.map(
                        ({ paso_nombre, umbrales, cantidad_tiempos }, idx) => (
                          <FormPaso
                            key={paso_nombre}
                            paso={paso_nombre}
                            umbrales={umbrales}
                            cantidadTiempos={cantidad_tiempos}
                            changePasosEditable={({ groupKey, key, value }) =>
                              changePasosEditable({
                                groupKey,
                                key,
                                value,
                                indice: idx,
                              })
                            }
                          />
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
            {listadoPasos && (
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveConfiguration}
                  disabled={sendingData}
                >
                  {t('apdex.save')}
                </Button>
              </Grid>
            )}
          </>
        )}
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert severity={snackbarData.severity}>{snackbarData.message}</Alert>
      </Snackbar>
    </Card>
  );
}

export default withRouter(withTranslation()(Apdex));
